/* @flow */

import * as React from 'react';
import Select, {Creatable, AsyncCreatable} from 'react-select';
import {api} from 'nutshell-core/api';
import {getSingularEntityType} from 'nutshell-core/utils/get-singular-entity-type';
import type {SelectMenuOption} from '../select-menu';

import {SelectPickerCreatable} from './select-picker-creatable';

type Props = {
    // These three props are required for creation
    allowCreate: boolean,
    creatableCollections: string[],
    onCreate: ({
        value: string,
        entityType: string,
    }) => any,

    name: string,
    label: string,
    onChange: (selectedOptions: Object[] | ?Object) => any,
    value: Object[] | ?Object,
    valueKey: string,
    labelKey: string,

    // This signifies an Async, multi-select
    loadOptions?: (query: string) => Promise<Object[]>,

    // Is this an array of objects, or just a single-select?
    multi?: boolean,

    // These are only required if loadOptions is not present
    options?: Object[],
    getRef?: (?Select) => any,

    autoBlur?: boolean,
};

type State = {
    secondaryNewOptions: SelectMenuOption[],
};

export class SelectPickerCreatableWrapper extends React.PureComponent<Props, State> {
    containerRef: HTMLElement;

    constructor() {
        super();
        this.state = {
            secondaryNewOptions: [],
        };
    }

    render() {
        const {value, options, valueKey, labelKey, multi, ...restProps} = this.props;
        const component = this.props.loadOptions ? AsyncCreatable : Creatable;
        let mappedOptions, mappedValue;
        if (options) {
            mappedOptions = options.map((opt) => ({
                ...opt,
                value: opt[valueKey],
                label: opt[labelKey],
            }));
        }
        if (value && multi && Array.isArray(value)) {
            mappedValue = value.map((opt) => ({
                ...opt,
                value: opt[valueKey],
                label: opt[labelKey],
            }));
        } else if (value && !multi && !Array.isArray(value)) {
            mappedValue = {...value, value: value[valueKey], label: value[labelKey]};
        }

        return (
            <SelectPickerCreatable
                {...restProps}
                component={component}
                multi={multi}
                options={mappedOptions}
                loadOptions={this.props.loadOptions ? this.loadOptions : undefined}
                value={mappedValue}
                valueKey='value'
                labelKey='label'
                formatCreateLabel={this.renderNewOptionText}
                onChange={multi ? this.handleChangeMulti : this.handleChangeSingle}
                onCreateOption={this.handleOptionCreation}
                onCreateNewEntity={this.createNewEntity}
                onCloseSelectMenu={this.handleCloseSelectMenu}
                secondaryNewOptions={this.state.secondaryNewOptions}
            />
        );
    }

    loadOptions = (query: string) => {
        return this.props.loadOptions
            ? this.props.loadOptions(query).then((options) =>
                  // $FlowFixMe upgrading Flow to v0.92.1 on web
                  options.map((opt) => ({
                      ...opt,
                      value: opt[this.props.valueKey],
                      label: opt[this.props.labelKey],
                  }))
              )
            : Promise.reject([]);
    };

    handleChangeMulti = (newOptions: Object[]) => {
        this.props.onChange(newOptions);
    };

    handleChangeSingle = (newOption: ?Object) => {
        newOption ? this.props.onChange(newOption) : this.props.onChange(null);
    };

    /*
     * Called by react-select's `formatCreateLabel` - this
     * overrides the built-in behavior of react-select.
     *
     * The return of this function gets passed into the
     * `renderNewOption` function below. By default,
     * react-select will return `new option ${label}`
     * and we don't want that.
     */
    renderNewOptionText = (label: string) => {
        return `Create ‟${label.trim()}”`;
    };

    /*
     * When creating a new option, this is called instead of `onChange`, which
     * gives us a chance to show a secondary menu if we need to.
     */
    handleOptionCreation = (newValue: string) => {
        // If the user can create more than one kind of entity, we need to
        // present a secondary menu
        if (this.props.creatableCollections.length > 1) {
            const secondaryNewOptions = this.getSelectMenuOptions(newValue);
            this.setState({secondaryNewOptions: secondaryNewOptions});

            return;
        }

        // Otherwise we can just create a new option with the single entity type
        const entityType = this.props.creatableCollections[0];
        this.handleClickCreatableOption(newValue, entityType);
    };

    /*
     * Sends a POST to the nutshell api to create the entity, and returns the
     * promise that resoles to the new option to show.
     */
    createNewEntity = (payload: Object, entityType: string) => {
        return api
            .post(entityType, {data: payload})
            .then((response) => response.json())
            .then((data) => {
                return data[entityType] && data[entityType][0];
            })
            .then((entity: Object) => {
                const newOptionValue = {
                    [this.props.valueKey]: entity[this.props.valueKey],
                    [this.props.labelKey]: entity[this.props.labelKey],
                    type: entityType,
                };

                // Callback to our component so they can let the
                // user know that an entity was created
                this.props.onCreate({
                    value: entity[this.props.labelKey],
                    entityType: getSingularEntityType(entityType),
                });

                return newOptionValue;
            });
    };

    /*
     * Creates an array of <SelectMenu/> options.
     */
    getSelectMenuOptions = (label: string) => {
        // $FlowFixMe upgrading Flow to v0.92.1 on web
        return this.props.creatableCollections.map((entityType: string) => {
            const singular = getSingularEntityType(entityType);

            return {
                entityType: entityType,
                label: `Create a ${singular} "${label}"`,
                option: {label},
                handleSelect: () => this.handleClickCreatableOption(label, entityType),
            };
        });
    };

    /*
     * This can be called in two different ways, either:
     *
     *  1)  From the `onCreateOption` handler when there is only one type of
     *      entity that the user can create.
     *  2)  From the `handleSelect` method of a secondary menu item, after the
     *      user has chosen what kind of entity they want to create.
     */
    handleClickCreatableOption = (newValue: string, entityType: string) => {
        const payload = {
            name: newValue,
        };
        this.createNewEntity(payload, entityType)
            .then((newOption) => {
                this.props.value && Array.isArray(this.props.value)
                    ? this.props.onChange(this.props.value.concat(newOption))
                    : this.props.onChange(newOption);
            })
            .catch(() => {
                this.props.onCreate({
                    value: payload[this.props.labelKey],
                    entityType: getSingularEntityType(entityType),
                    type: 'error',
                });
            });
    };

    /*
     * This method is called by the secondary menu when it closes
     */
    handleCloseSelectMenu = () => {
        this.setState({secondaryNewOptions: []});
    };
}
